import { ArrowBackIcon, ArrowForwardIcon, CheckIcon, PhoneIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  Flex,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import { ScreenContext, useApp, useAppModals, useAuth, useThreadsList } from '@hb/react'
import {
  ActionButton,
  AssessmentContext,
  AssessmentIcon,
  BoxHeader,
  BoxSubheader2,
  EditorModalContainer,
  FloatingContainer,
  FormWizard,
  InlineLink,
  ReadOnlyEditor,
  renderStageWithInsuranceInfo,
  SolidActionButton,
  updateAssessmentAnswers,
  updateAssessmentViewed,
  useFormsView,
  useResizeObserver,
} from '@hb/react-web'
import {
  ASSESSMENTS,
  AssessmentStepKey,
  Descendant,
  FieldMapValue,
  getAvailableForms,
  getDateString,
  getQuestionnaireSummaryComplete,
  PopulatedAssessment,
  UpdateCallback,
  v106releaseDate,
} from '@hb/shared'
import { PropsWithChildren, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { AssessmentDisclaimersModal } from './AssessmentDisclaimers'
import { AssessmentFormPreview } from './FormPreview'
import { PatientStatementsSection } from './PatientStatementsSection'
import { SignOnPanel } from './SignOn/SignOnPanel'

const AssessmentResultsModal = ({
  isOpen,
  onClose,
  onProceed,
  editorVersion,
  results,
}: {
  isOpen: boolean
  onClose: () => void
  onProceed: () => void
  editorVersion: 'v1' | 'v2'
  results: Descendant[]
}) => {
  const { height: screenHeight } = useContext(ScreenContext)
  const buttonsRef = useRef<HTMLDivElement>(null)
  const { height: buttonsHeight } = useResizeObserver(buttonsRef, 'content')
  const {
    signOnData: { signOnStagesComplete },
    assessment,
  } = useFormsView()
  const { disclaimers } = assessment.assessmentData ?? {}
  const { disclaimer1Accepted, disclaimer2Accepted } = disclaimers ?? { 1: false, 2: false }

  const [disclaimersOpened, setDisclaimersOpened] = useState(false)
  const handleDisclaimersOpened = useCallback(() => {
    setDisclaimersOpened(true)
  }, [])
  const handleDisclaimersClosed = useCallback(() => {
    if (!disclaimersOpened || !disclaimer1Accepted || !disclaimer2Accepted) onClose()
    else setDisclaimersOpened(false)
  }, [disclaimersOpened, onClose, disclaimer1Accepted, disclaimer2Accepted])

  const maxHeight = useMemo(() => screenHeight - 80 - buttonsHeight, [buttonsHeight, screenHeight])
  if (!disclaimer1Accepted || !disclaimer2Accepted || disclaimersOpened) {
    return <AssessmentDisclaimersModal isOpen={isOpen} onClose={handleDisclaimersClosed} />
  }

  return (
    <EditorModalContainer
      isOpen={isOpen}
      onClose={onClose}
      height={maxHeight}
      render={(sizeProps, onBack) => (
        <Flex maxH={screenHeight} flexFlow="column" w="100%">
          <Box minH="0" flex={1} w="100%">
            <ReadOnlyEditor {...sizeProps} version={editorVersion} value={results} />
          </Box>
          <Stack
            ref={buttonsRef}
            gap={[1, 1, 2]}
            bg="gray.50"
            // direction={['column', 'column', 'row']}
            direction="row"
            zIndex={1}
            justifyContent={['flex-start', 'flex-start', 'space-between']}
            p={[2, 2, 3]}
            borderTop="1px solid #cdcdcd"
            w="100%">
            <ActionButton bg="white" size="sm" onClick={onBack} variant="outline">
              <ArrowBackIcon mr={2} />
              <Text>Back</Text>
            </ActionButton>
            <ActionButton bg="white" size="sm" onClick={handleDisclaimersOpened} variant="outline">
              Disclaimers
            </ActionButton>
            <SolidActionButton size="sm" flex={1} gap={2} onClick={onProceed}>
              {signOnStagesComplete ? (
                <CheckIcon filter="drop-shadow(1px 1px 3px #00000055)" />
              ) : null}
              {signOnStagesComplete ? 'Signed on' : 'Next Steps'}
              {signOnStagesComplete ? null : (
                <ArrowForwardIcon filter="drop-shadow(1px 1px 3px #00000055)" w={5} h={5} />
              )}
            </SolidActionButton>
          </Stack>
        </Flex>
      )}
    />
  )
}

const CallScheduledView = () => (
  <BoxSubheader2>
    You have scheduled your call with Hamilton Billing! A dedicated HB team member will walk you
    through your assessment in detail during call.
  </BoxSubheader2>
)

const ScheduleCallView = () => {
  const { scheduleCall } = useAppModals()
  return (
    <Stack direction={['column', 'column', 'row']} pb={3} w="100%">
      <BoxSubheader2 flex={1}>
        Click to schedule a call with Hamilton Billing so we can go over your assessment and answer
        any questions you may have.
      </BoxSubheader2>
      <SolidActionButton
        w={['100%', '100%', 'auto']}
        gap={1.5}
        onClick={() => scheduleCall.open('FormsView')}>
        <PhoneIcon w={4} h={4} filter="drop-shadow(1px 1px 3px #00000055)" />
        <Text fontFamily="Open Sans" fontWeight={600}>
          SCHEDULE A CALL
        </Text>
      </SolidActionButton>
    </Stack>
  )
}

const PrerequisteView = ({
  title,
  complete,
  children,
}: PropsWithChildren<{ title: string; complete: boolean }>) => (
  <Flex flexFlow="column" py={1} w="100%">
    <Flex py={1} align="center" gap={2} px={1}>
      {complete ? <Checkbox colorScheme="green" size="lg" isChecked /> : null}
      <Text fontSize="lg" fontWeight={500} opacity={0.8}>
        {title}
      </Text>
    </Flex>
    <Box px={2}>{children}</Box>
  </Flex>
)

export const ScheduledCallStatusView = ({
  assessment,
}: {
  assessment: PopulatedAssessment | null
}) => {
  const { scheduledCall, sentOn } = assessment ?? {}
  const showScheduleCall = useMemo(() => {
    if (!sentOn || !!scheduledCall) return false
    return true
  }, [sentOn, scheduledCall])

  return (
    <>
      <Collapse style={{ width: '100%' }} in={showScheduleCall}>
        <ScheduleCallView />
      </Collapse>
      <Collapse style={{ width: '100%' }} in={!!scheduledCall}>
        <CallScheduledView />
      </Collapse>
    </>
  )
}

const AssessmentCompletePrerequisites = ({
  assessment,
  canView,
  handleViewClick,
}: {
  canView: boolean
  handleViewClick: () => void
  assessment: PopulatedAssessment | null
}) => (
  <Flex flexFlow="column" w="100%" py={3}>
    <ViewAssessmentButton canView={canView} assessment={assessment} onClick={handleViewClick} />
  </Flex>
)

const ScheduleCallPrerequisite = ({ assessment }: { assessment: PopulatedAssessment | null }) => (
  <>
    {assessment?.viewableBeforeCall ? <Divider /> : null}
    <PrerequisteView
      title="Schedule a call with Hamilton Billing"
      complete={!!assessment?.scheduledCall}>
      <ScheduledCallStatusView assessment={assessment} />
    </PrerequisteView>
    {assessment?.viewableBeforeCall ? null : <Divider />}
  </>
)

const ViewAssessmentButton = ({
  canView,
  assessment,
  onClick,
}: {
  canView: boolean
  assessment: PopulatedAssessment | null
  onClick: () => void
}) => {
  const tooltipText = useMemo(() => {
    if (
      !assessment?.viewableBeforeCall &&
      !!assessment?.scheduledCall?.scheduledOn &&
      !assessment?.scheduledCall?.callDate
    )
      return 'You have scheduled a call with Hamilton Billing. Your assessment will be available once you start your call.'
    return 'Please complete the above steps to view your assessment'
  }, [assessment])
  const body = (
    <SolidActionButton
      w="100%"
      isDisabled={!canView}
      // size='sm'
      onClick={onClick}>
      <Flex alignItems="center" gap={1}>
        <AssessmentIcon opacity={1} filter={'drop-shadow(1px 1px 3px #00000055)'} />
        <Text>VIEW ASSESSMENT</Text>
      </Flex>
    </SolidActionButton>
  )
  return !canView ? (
    <Tooltip
      hasArrow
      placement="top"
      fontSize="md"
      lineHeight={1.2}
      pb={2}
      textAlign="center"
      label={tooltipText}>
      {body}
    </Tooltip>
  ) : (
    body
  )
}

const AssessmentCompleteView = ({
  assessment,
  assessmentId,
  setViewSignOnQuestionnaire,
}: {
  assessment: PopulatedAssessment | null
  assessmentId: string
  setViewSignOnQuestionnaire: (view: boolean) => void
}) => {
  const { selectStep } = useFormsView()
  const authUser = useAuth(s => s.authUser)
  const {
    results,
    editorVersion,
    patientId,
    scheduledCall,
    resultsViewedOn,
    viewableBeforeCall,
    sentOn,
  } = assessment ?? {}
  const [viewAssessment, setViewAssessment] = useState(false)

  const [resultsViewedOverride, setResultsViewedOverride] = useState<number | undefined>()
  const assessmentViewed = !!(resultsViewedOn || resultsViewedOverride)
  const canSignOn = assessmentViewed && !!scheduledCall?.reportedOn

  const usedVersion = editorVersion ?? 'v1'

  const canView = useMemo(() => {
    if (sentOn && sentOn < v106releaseDate) return true
    if (!scheduledCall?.callDate && !viewableBeforeCall) return false
    return true
  }, [scheduledCall, viewableBeforeCall, sentOn])

  const handleViewClick = useCallback(() => {
    if (!canView || !assessment) return
    if (!assessment.resultsViewedOn && patientId === authUser?.uid) {
      setResultsViewedOverride(Date.now())
      updateAssessmentViewed({ id: assessmentId })
    }
    setViewAssessment(true)
  }, [assessment, assessmentId, canView, patientId, authUser])

  const onProceed = useCallback(() => {
    if (scheduledCall?.reportedOn) {
      selectStep('signOn')
    }
    setViewAssessment(false)
    setViewSignOnQuestionnaire(true)
  }, [setViewSignOnQuestionnaire, selectStep, scheduledCall])
  return (
    <FloatingContainer mb={4}>
      <BoxHeader>
        {!canView ? 'We have reviewed your coverage!' : 'Your assessment is ready!'}
      </BoxHeader>
      {viewableBeforeCall ? (
        <Text fontSize="lg">
          Please take a look at your assessment and then schedule a call with Hamilton Billing to go
          over the details.
        </Text>
      ) : (
        <ScheduleCallPrerequisite assessment={assessment} />
      )}
      <AssessmentCompletePrerequisites
        assessment={assessment}
        canView={canView}
        handleViewClick={handleViewClick}
      />
      <Stack spacing={1} w="100%" direction="column">
        {viewableBeforeCall ? <ScheduleCallPrerequisite assessment={assessment} /> : null}
        <Collapse in={assessmentViewed} style={{ width: '100%' }}>
          <Divider />
          <Box w="100%" py={3}>
            <SolidActionButton
              w="100%"
              onClick={() => {
                selectStep('signOn')
              }}
              isDisabled={!canSignOn}
              opacity={assessmentViewed ? 1 : 0}
              pointerEvents={assessmentViewed ? 'auto' : 'none'}>
              PROCEED TO SIGN ON
            </SolidActionButton>
          </Box>
        </Collapse>
      </Stack>
      {results && viewAssessment ? (
        <AssessmentResultsModal
          results={results}
          editorVersion={usedVersion}
          isOpen={viewAssessment}
          onProceed={onProceed}
          onClose={() => {
            setViewAssessment(false)
          }}
        />
      ) : null}
    </FloatingContainer>
  )
}

const AssessmentViewStage = ({
  assessment,
  assessmentId,
  setViewSignOnQuestionnaire,
}: {
  assessment: PopulatedAssessment | null
  assessmentId: string
  setViewSignOnQuestionnaire: (view: boolean) => void
}) => {
  const { sentOn } = assessment ?? {}

  return (
    <Collapse style={{ width: '100%' }} in={!!sentOn}>
      <AssessmentCompleteView
        assessment={assessment}
        assessmentId={assessmentId}
        setViewSignOnQuestionnaire={setViewSignOnQuestionnaire}
      />
    </Collapse>
  )
}

const ReadOnlyFooter = () => {
  const { onOpen } = useThreadsList()
  return (
    <Flex px={4} py={3}>
      <Text>
        You've already submitted this assessment,{' '}
        <InlineLink onClick={() => onOpen()}>get in touch</InlineLink> with us if you need to edit
        any information!
      </Text>
    </Flex>
  )
}

const ContactTeamBox = () => {
  const { onOpen } = useThreadsList()
  return (
    <FloatingContainer mb={4}>
      <Text px={1} fontSize="md">
        If there are any changes to your pregnancy or insurance info, please{' '}
        <InlineLink onClick={() => onOpen()}>message our team</InlineLink> as soon as possible and
        we will update your information.
      </Text>
    </FloatingContainer>
  )
}

const QuestionnaireSummaryComplete = () => {
  const { assessment, selectStep } = useFormsView()
  const { scheduleCallNotificationSentOn, scheduledCall, viewableBeforeCall } =
    assessment?.populated ?? {}
  const { scheduleCall } = useAppModals()
  const text = useMemo(() => {
    if (scheduledCall?.callDate) {
      return `You had a call with Hamilton Billing on ${getDateString(scheduledCall.callDate)}. Feel free to reach out with any other questions via the chat feature in the bottom right corner of your screen.`
    } else if (scheduledCall?.scheduledOn) {
      return 'You have scheduled a call with Hamilton Billing. Your assessment will be available once you complete the disclaimers in the next step and start your call.'
    } else if (viewableBeforeCall) {
      return (
        <Text fontSize="lg">
          Your assessment is complete! Please{' '}
          <Button color="green.500" variant="link" onClick={() => selectStep('view')}>
            view your assessment
          </Button>{' '}
          and then schedule a call with Hamilton Billing to go over the details.
        </Text>
      )
    } else if (scheduleCallNotificationSentOn) {
      return (
        <Text fontSize="lg">
          Your assessment is complete! Please{' '}
          <Button color="green.500" variant="link" onClick={() => scheduleCall.open('FormsView')}>
            Schedule a Call
          </Button>{' '}
          with Hamilton Billing to go over your assessment.
        </Text>
      )
    } else {
      return 'Thank you for submitting your info so we can research your plan’s coverage. Please keep an \
      eye out for an email from Hamilton Billing for next steps, at which point you can schedule a \
      call with the appropriate HB team member to go over coverage from your plan.'
    }
  }, [scheduleCallNotificationSentOn, scheduledCall, scheduleCall, selectStep, viewableBeforeCall])
  return (
    <FloatingContainer my={4}>
      <BoxHeader>Your Questionnaire is Complete!</BoxHeader>
      {typeof text === 'string' ? (
        <Text px={1} fontSize="lg">
          {text}
        </Text>
      ) : (
        text
      )}
    </FloatingContainer>
  )
}

const AssessmentQuestionsStage = ({
  stage,
  assessmentIndex,
}: {
  stage: AssessmentStepKey
  assessmentIndex: number | null
}) => {
  const { assessment } = useFormsView()
  const { populated: populatedAssessment, assessmentData } = assessment
  const { skippedQuestionnaire, sentOn } = populatedAssessment ?? {}
  const availableForms = useMemo(
    () => getAvailableForms(false, populatedAssessment),
    [populatedAssessment],
  )

  const questionnaireSummaryComplete = useMemo(
    () => getQuestionnaireSummaryComplete(assessmentData?.data ?? {}),
    [assessmentData],
  )

  const showSummaryComplete = useMemo(() => {
    if (stage !== 'questions') return false
    if (sentOn) return true
    return questionnaireSummaryComplete
  }, [questionnaireSummaryComplete, stage, sentOn])

  return (
    <>
      <Collapse style={{ width: '100%' }} in={showSummaryComplete}>
        <QuestionnaireSummaryComplete />
      </Collapse>
      <Collapse style={{ width: '100%' }} in={!skippedQuestionnaire && stage === 'questions'}>
        <Stack w="100%" spacing={1} mb={2} direction="column">
          {assessmentData && availableForms
            ? availableForms.map(({ id: formId }, index: number) => (
                <AssessmentFormPreview
                  assessmentIndex={assessmentIndex}
                  key={formId}
                  first={index === 0}
                  formId={formId}
                />
              ))
            : null}
        </Stack>
      </Collapse>
    </>
  )
}

export const FormsViewAssessment: React.FC<{
  assessmentIndex: number | null
  assessmentId: string
  stage: AssessmentStepKey
}> = ({ assessmentIndex, stage, assessmentId }) => {
  const {
    populatedUser: userData,
    assessment,
    formData,
    formCorrections,
    form,
    selected,
    open,
    close,
    userId,
  } = useFormsView()
  const { user } = userData ?? {}
  const { populated: populatedAssessment, assessmentData } = assessment
  const { sentOn, resultsViewedOn, skippedQuestionnaire } = populatedAssessment ?? {}

  const { appName } = useApp()

  const getStoragePath = useCallback(
    (stageId: string) => {
      if (!user?.id) throw new Error('No user ID')
      switch (stageId) {
        case 'delivery':
          return `${ASSESSMENTS}/${assessmentId}/data/delivery`
        case 'insuranceInfo':
          return `${ASSESSMENTS}/${assessmentId}/data/insuranceInfo`
        case 'insurancePlans':
          return `${ASSESSMENTS}/${assessmentId}/data/insurancePlans`
        default:
          return null
      }
    },
    [user, assessmentId],
  )

  const onSubmit = useCallback(
    async (data: FieldMapValue): Promise<UpdateCallback> => {
      try {
        if (!assessmentId) return { error: 'No assessment ID' }
        await updateAssessmentAnswers({ id: assessmentId, data, appName })
      } catch (err: any) {
        console.error(err)
        throw new Error(`Error saving answers: ${err.message ?? 'Unknown error'}`)
      }
      return { success: 'Saved!' }
    },
    [assessmentId, appName],
  )

  const [viewSignOnQuestionnaire, setViewSignOnQuestionnaire] = useState(false)
  return (
    <AssessmentContext.Provider value={populatedAssessment}>
      <Collapse style={{ width: '100%' }} in={stage === 'view' || stage === 'signOn'}>
        <ContactTeamBox />
      </Collapse>
      <Collapse
        style={{ width: '100%' }}
        in={!skippedQuestionnaire && stage === 'view' && !!populatedAssessment?.sentOn}>
        <AssessmentViewStage
          setViewSignOnQuestionnaire={setViewSignOnQuestionnaire}
          assessment={populatedAssessment}
          assessmentId={assessmentId}
        />
      </Collapse>
      <AssessmentQuestionsStage stage={stage} assessmentIndex={assessmentIndex} />
      {form ? (
        <FormWizard
          stageId={selected?.stageId}
          ReadOnlyFooter={ReadOnlyFooter}
          readOnly={!!assessmentData?.submittedOn}
          getStoragePath={getStoragePath}
          renderStage={renderStageWithInsuranceInfo}
          corrections={formCorrections}
          data={formData}
          patientId={userId}
          assessmentId={assessmentId}
          selectStage={stageId => {
            if (!selected) return
            if (!stageId) close()
            else open(selected.formId, stageId)
          }}
          onSubmit={onSubmit}
          form={form}
        />
      ) : null}

      <Collapse style={{ width: '100%' }} in={skippedQuestionnaire || stage === 'signOn'}>
        {skippedQuestionnaire || (sentOn && (resultsViewedOn || viewSignOnQuestionnaire)) ? (
          <SignOnPanel user={userData?.user ?? null} assessment={populatedAssessment} />
        ) : null}
        {stage === 'signOn' && userId && populatedAssessment?.midwifeId ? (
          <PatientStatementsSection
            assessmentId={assessmentId}
            patientId={userId}
            practiceId={populatedAssessment.midwifeId}
          />
        ) : null}
      </Collapse>
    </AssessmentContext.Provider>
  )
}
